import type { FilterGroup } from '@Core/components/dynamicFields/filter/types';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult
} from '@tanstack/react-query';
import axios from 'axios';

export type SharedFiltersResponse = Diagonal.Data.Data.Api.SharedFiltersResponseData;
export type SharedFilter = SharedFiltersResponse['data'][number];

export type SharedFiltersQueryParams = {
  resourceId?: string | null;
  enabled?: boolean;
};

function getSharedFiltersQueryKey(resourceId?: string | null) {
  return ['shared-filters', resourceId ?? null] as const;
}

export function useSharedFilters({
  resourceId,
  enabled = true
}: SharedFiltersQueryParams): UseQueryResult<SharedFiltersResponse> {
  return useQuery({
    queryKey: getSharedFiltersQueryKey(resourceId),
    queryFn: async () => {
      // A viewer without the Index Records scope gets a 403 here and on the record
      // queries alike; treat it as "no shared filters" so the grid's own policy error
      // is the only surfaced failure.
      const response = await axios.get(`/api/data/resources/${resourceId}/shared-filters`, {
        validateStatus: (status) => status === 200 || status === 403
      });

      if (response.status === 403) {
        return { data: [], can_create: false, can_share: false } satisfies SharedFiltersResponse;
      }

      return response.data;
    },
    enabled: enabled && !!resourceId
  });
}

/** A validation failure saving a filter (e.g. a duplicate name), for inline display. */
export class SharedFilterSaveError extends Error {}

/**
 * The optional view state persisted alongside a filter's conditions. `null` pieces
 * mean "not configured — leave the consumer's default alone".
 */
export type SharedFilterViewInput = {
  sort?: { key: string; direction: 'asc' | 'desc' }[] | null;
  columns?: string[] | null;
  search?: string | null;
  per_page?: number | null;
};

export type CreateSharedFilterInput = SharedFilterViewInput & {
  name: string;
  filter: FilterGroup | null;
  /**
   * Sharing scope; defaults to private. Only honored for viewers with the
   * manage-shared-filters permission — the endpoint forces every other
   * identity's filter private regardless of the flag sent.
   */
  is_private?: boolean;
};

/**
 * Creates a shared filter on the resource — private by default. The created filter
 * is appended to the cached shared-filters list so it is selectable immediately.
 */
export function useCreateSharedFilter(
  resourceId: string | null
): UseMutationResult<SharedFilter, Error, CreateSharedFilterInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, filter, is_private, ...viewState }: CreateSharedFilterInput) => {
      // Expected validation failures (duplicate name, bad filter) surface inline in
      // the save popover; accepting 422 keeps them away from global error handling.
      const response = await axios.post(
        `/api/data/resources/${resourceId}/shared-filters`,
        { name, is_private: is_private ?? true, filter, ...viewState },
        { validateStatus: (status) => (status >= 200 && status < 300) || status === 422 }
      );

      if (response.status === 422) {
        const errors = (response.data as { errors?: Record<string, string[]> }).errors ?? {};
        const message = Object.values(errors).flat()[0];
        throw new SharedFilterSaveError(
          typeof message === 'string' && message !== '' ? message : 'Could not save the filter.'
        );
      }

      return response.data as SharedFilter;
    },
    onSuccess: (created) => {
      const queryKey = getSharedFiltersQueryKey(resourceId);
      queryClient.setQueryData<SharedFiltersResponse>(queryKey, (previous) =>
        previous
          ? {
              ...previous,
              data: [...previous.data, created].toSorted((a, b) => a.name.localeCompare(b.name))
            }
          : previous
      );
      void queryClient.invalidateQueries({ queryKey });
    }
  });
}

export type UpdateSharedFilterInput = SharedFilterViewInput & {
  sharedFilterId: string;
  name?: string;
  filter?: FilterGroup | null;
  /**
   * Sharing scope. Only honored for viewers with the manage-shared-filters
   * permission — the endpoint ignores the flag for every other identity.
   */
  is_private?: boolean;
};

/**
 * Updates a shared filter the viewer manages (their own; any filter for holders of
 * the manage-shared-filters permission). The cached shared-filters list is patched
 * in place so the change shows immediately.
 */
export function useUpdateSharedFilter(
  resourceId: string | null
): UseMutationResult<SharedFilter, Error, UpdateSharedFilterInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sharedFilterId, ...payload }: UpdateSharedFilterInput) => {
      // Expected validation failures (duplicate name, bad filter) surface inline in
      // the rename popover; accepting 422 keeps them away from global error handling.
      const response = await axios.put(
        `/api/data/resources/${resourceId}/shared-filters/${sharedFilterId}`,
        payload,
        { validateStatus: (status) => (status >= 200 && status < 300) || status === 422 }
      );

      if (response.status === 422) {
        const errors = (response.data as { errors?: Record<string, string[]> }).errors ?? {};
        const message = Object.values(errors).flat()[0];
        throw new SharedFilterSaveError(
          typeof message === 'string' && message !== '' ? message : 'Could not save the filter.'
        );
      }

      return response.data as SharedFilter;
    },
    onSuccess: (updated) => {
      const queryKey = getSharedFiltersQueryKey(resourceId);
      queryClient.setQueryData<SharedFiltersResponse>(queryKey, (previous) =>
        previous
          ? {
              ...previous,
              data: previous.data
                .map((sharedFilter) => (sharedFilter.id === updated.id ? updated : sharedFilter))
                .toSorted((a, b) => a.name.localeCompare(b.name))
            }
          : previous
      );
      void queryClient.invalidateQueries({ queryKey });
    }
  });
}

/**
 * Deletes a shared filter the viewer manages and drops it from the cached list.
 */
export function useDeleteSharedFilter(
  resourceId: string | null
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sharedFilterId: string) => {
      await axios.delete(`/api/data/resources/${resourceId}/shared-filters/${sharedFilterId}`);
    },
    onSuccess: (_result, sharedFilterId) => {
      const queryKey = getSharedFiltersQueryKey(resourceId);
      queryClient.setQueryData<SharedFiltersResponse>(queryKey, (previous) =>
        previous
          ? {
              ...previous,
              data: previous.data.filter((sharedFilter) => sharedFilter.id !== sharedFilterId)
            }
          : previous
      );
      void queryClient.invalidateQueries({ queryKey });
    }
  });
}
